import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { formatNumberWithCommasAndDecimals } from '../../../sales-tax/utils';

export interface FilingBreakdownProps {
  breakdown: {
    taxDue?: number;
    tax_liability?: number;
    taxPaid?: number;
    taxCollected?: number;
    filingDate?: string;
    taxableSales?: number;
    nonTaxableSales?: number;
    marketplaceSales?: number;
    grossSales?: number;
    exemptSales?: number;
    excludedSales?: number;
    numberOfTransactions?: number;
    adjustments?: {
      prepayment?: number;
      priorPayment?: number;
      retailDeliveryFee?: number;
      jurisdictionCredit?: number;
      vendorDiscount?: number;
      rounding?: number;
      penalty?: number;
      interest?: number;
      marketplacePayment?: number;
    };
  };
}

export const FilingBreakdownAccordion = ({
  breakdown,
}: FilingBreakdownProps) => {
  const renderAmount = (amount: number | undefined, forceNegative = false) => {
    if (amount === undefined) return null;

    return (
      <span className="text-text-primary txt-small-medium">
        <span className="text-text-quaternary txt-small-medium mr-xxs">$</span>
        {forceNegative ? '- ' : ''}
        {formatNumberWithCommasAndDecimals(Math.abs(amount))}
      </span>
    );
  };

  const renderBreakdownItem = (
    label: string,
    amount: number | undefined,
    forceNegative = false
  ) => {
    if (amount === undefined) return null;

    return (
      <div className="flex justify-between">
        <span className="text-text-tertiary txt-small-medium">{label}</span>
        {renderAmount(amount, forceNegative)}
      </div>
    );
  };

  const formatDateString = (dateString: string): string => {
    if (!dateString) return '';
    const date = NonTimezoneSensitiveDate.fromISO8601String(dateString);
    return date.format('MMM D, YYYY');
  };

  return (
    <div className="flex flex-col gap-2xl">
      <Accordion type="single" collapsible defaultValue="breakdown">
        <AccordionItem className="border-none bg-transparent" value="breakdown">
          <AccordionTrigger className="p-0">
            <p className="txt-small-bold text-text-primary">Breakdown</p>
          </AccordionTrigger>
          <AccordionContent className="p-0 pt-md">
            <div className="flex flex-col gap-lg">
              {/* {renderBreakdownItem('Tax due', breakdown.taxDue)} */}
              {/* {renderBreakdownItem('Tax paid', breakdown.taxPaid)} */}
              {renderBreakdownItem('Tax collected', breakdown.taxCollected)}

              {breakdown.filingDate && (
                <div className="flex justify-between">
                  <span className="text-text-tertiary txt-small-medium">
                    Filing date
                  </span>
                  <span className="text-text-primary txt-small-medium">
                    {breakdown.filingDate
                      ? formatDateString(breakdown.filingDate)
                      : 'Filing date not available'}
                  </span>
                </div>
              )}

              {renderBreakdownItem('Gross sales', breakdown.grossSales)}

              {renderBreakdownItem('Taxable sales', breakdown.taxableSales)}

              {renderBreakdownItem(
                'Non-taxable sales',
                breakdown.nonTaxableSales,
                false
              )}

              {renderBreakdownItem(
                'Marketplace sales',
                breakdown.marketplaceSales,
                false
              )}

              {renderBreakdownItem(
                'Exempt sales',
                breakdown.exemptSales,
                false
              )}

              {renderBreakdownItem(
                'Excluded sales',
                breakdown.excludedSales,
                true
              )}

              {breakdown.numberOfTransactions !== undefined && (
                <div className="flex justify-between">
                  <span className="text-text-tertiary txt-small-medium">
                    No. of transactions
                  </span>
                  <span className="text-text-primary txt-small-medium">
                    {breakdown.numberOfTransactions.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {breakdown.adjustments &&
        Object.values(breakdown.adjustments).some((value) => value !== 0) && (
          <Accordion type="single" collapsible defaultValue="adjustments">
            <AccordionItem
              className="border-none bg-transparent"
              value="adjustments"
            >
              <AccordionTrigger className="p-0">
                <p className="txt-small-bold text-text-primary">Adjustments</p>
              </AccordionTrigger>
              <AccordionContent className="pb-xl pt-md">
                <div className="flex flex-col gap-lg">
                  {breakdown.adjustments.prepayment !== undefined &&
                    breakdown.adjustments.prepayment !== 0 &&
                    renderBreakdownItem(
                      'Prepayment',
                      breakdown.adjustments.prepayment
                    )}

                  {breakdown.adjustments.priorPayment !== undefined &&
                    breakdown.adjustments.priorPayment !== 0 &&
                    renderBreakdownItem(
                      'Prior payment',
                      breakdown.adjustments.priorPayment,
                      true
                    )}

                  {breakdown.adjustments.retailDeliveryFee !== undefined &&
                    breakdown.adjustments.retailDeliveryFee !== 0 &&
                    renderBreakdownItem(
                      'Retail delivery fee',
                      breakdown.adjustments.retailDeliveryFee
                    )}

                  {breakdown.adjustments.jurisdictionCredit !== undefined &&
                    breakdown.adjustments.jurisdictionCredit !== 0 &&
                    renderBreakdownItem(
                      'Jurisdiction-issued credit',
                      breakdown.adjustments.jurisdictionCredit,
                      true
                    )}

                  {breakdown.adjustments.marketplacePayment !== undefined &&
                    breakdown.adjustments.marketplacePayment !== 0 &&
                    renderBreakdownItem(
                      'Marketplace payment',
                      breakdown.adjustments.marketplacePayment,
                      true
                    )}

                  {breakdown.adjustments.vendorDiscount !== undefined &&
                    breakdown.adjustments.vendorDiscount !== 0 &&
                    renderBreakdownItem(
                      'Vendor discount',
                      breakdown.adjustments.vendorDiscount,
                      true
                    )}

                  {breakdown.adjustments.rounding !== undefined &&
                    breakdown.adjustments.rounding !== 0 &&
                    renderBreakdownItem(
                      'Rounding',
                      breakdown.adjustments.rounding
                    )}

                  {breakdown.adjustments.penalty !== undefined &&
                    breakdown.adjustments.penalty !== 0 &&
                    renderBreakdownItem(
                      'Penalty',
                      breakdown.adjustments.penalty
                    )}

                  {breakdown.adjustments.interest !== undefined &&
                    breakdown.adjustments.interest !== 0 &&
                    renderBreakdownItem(
                      'Interest',
                      breakdown.adjustments.interest
                    )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
    </div>
  );
};
